import { useParams, Link } from "react-router-dom";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import { getIndustryData } from "@/data/industryPages.mjs";
import IndustryIllustrations from "@/components/illustrations/IndustryIllustrations";
import {
  ChallengesIllustration,
  SetupIllustration,
  SolutionsIllustration,
  WhyItMattersIllustration,
  FAQIllustration,
} from "@/components/illustrations/SectionIllustrations";
import Reveal from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";
import NotFound from "@/pages/NotFound";
import {
  ArrowRight,
  ChevronRight,
  Check,
  AlertTriangle,
  HelpCircle,
} from "lucide-react";

const SECTION_PY = "py-14 lg:py-16"; // tighter than the default `section` (py-20 lg:py-24)

export default function IndustryPage() {
  const { slug } = useParams();
  const data = getIndustryData(slug);
  if (!data) return <NotFound />;

  const pagePath = `/industries/${slug}`;
  const Illus = IndustryIllustrations[data.illustrationKey] || null;

  return (
    <div data-testid={`industry-page-${slug}`}>
      <Seo {...getSeoForPath(pagePath)} />

      {/* HERO — unified navy header, matches CashDiscount / Syracuse patterns */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 55% 65% at 78% 50%, rgba(0,114,255,0.30) 0%, transparent 65%)" }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 40% 50% at 20% 30%, rgba(0,114,255,0.14) 0%, transparent 60%)" }} />
        <div aria-hidden="true" className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(110px)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 right-[12%] w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(120px)" }} />

        <ParticleField density={36} connectDistance={130} className="absolute inset-0 w-full h-full" />

        <div className="container-swipe relative pt-14 pb-16 lg:pt-20 lg:pb-24">
          <nav className="flex items-center gap-2 text-[13px] text-white/50 mb-8" data-testid="industry-breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/industries" className="hover:text-white">Industries</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/80">{data.eyebrow.replace(/^For\s+/, "")}</span>
          </nav>

          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-10 lg:gap-12 items-center">
            <Reveal>
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                  {data.eyebrow}
                </div>
                <h1 className="font-display-tight text-[2.5rem] sm:text-[3.25rem] lg:text-[4rem] xl:text-[4.25rem] font-extrabold leading-[1.05] text-white tracking-[-0.02em]">
                  {data.h1}
                </h1>
                <p className="mt-7 text-[17px] md:text-[18px] text-white/75 leading-[1.7] max-w-xl">
                  {data.heroBody}
                </p>
                <div className="mt-9 flex flex-wrap gap-3">
                  <Link to="/get-started" className="btn-primary-swipe text-[15px] py-3.5 px-7" data-testid={`industry-${slug}-hero-get-started`}>
                    Get Started <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/savings-analysis" className="btn-outline-light text-[15px] py-3.5 px-7" data-testid={`industry-${slug}-hero-savings`}>
                    Request a Savings Analysis
                  </Link>
                </div>
              </div>
            </Reveal>

            <Reveal variant="slide-right" delay={120}>
              <div className="relative rounded-2xl overflow-hidden bg-white/95 border border-white/10 shadow-[0_18px_44px_-16px_rgba(0,0,0,0.45)]">
                {Illus ? <Illus /> : null}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTION — Challenges */}
      <section className={`${SECTION_PY} bg-white relative overflow-hidden`}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-40" style={{ backgroundImage: "radial-gradient(circle at 1.5px 1.5px, rgba(0,114,255,0.09) 1.5px, transparent 0)", backgroundSize: "24px 24px" }} />
        <div aria-hidden="true" className="absolute -top-24 -right-32 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.08)", filter: "blur(90px)" }} />
        <div aria-hidden="true" className="absolute -bottom-24 -left-16 w-[320px] h-[320px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.06)", filter: "blur(80px)" }} />
        <div className="container-swipe relative">
          <div className="grid lg:grid-cols-[1.15fr,1fr] gap-8 lg:gap-12 items-center">
            <div>
              <Reveal><div className="overline mb-3">Payment Challenges</div></Reveal>
              <Reveal delay={80}>
                <h2 className="font-display-tight text-[1.85rem] sm:text-[2.25rem] lg:text-[2.5rem] font-extrabold text-[var(--swipe-navy)] leading-[1.15]">
                  {data.challengesHeading}
                </h2>
              </Reveal>
            </div>
            <Reveal variant="slide-right" delay={140}>
              <div className="hidden lg:block"><ChallengesIllustration /></div>
            </Reveal>
          </div>

          <div className="mt-8 grid md:grid-cols-2 gap-4">
            {data.challenges.map((c, i) => (
              <Reveal key={i} delay={80 + i * 40}>
                <div className="relative h-full bg-white border border-[var(--swipe-accent)]/20 rounded-2xl p-5 flex gap-4 overflow-hidden transition-all duration-300 hover:border-[var(--swipe-accent)]/50 hover:shadow-[0_18px_44px_-22px_rgba(0,114,255,0.45)]">
                  <div aria-hidden="true" className="absolute -top-6 -right-6 w-16 h-16 rounded-full bg-[var(--swipe-accent)]/8" />
                  <div className="relative shrink-0 w-10 h-10 rounded-xl bg-[var(--swipe-accent)] text-white flex items-center justify-center font-black text-[15px]">
                    {i + 1}
                  </div>
                  <p className="relative text-[15px] text-slate-700 leading-[1.65]">{c}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION — How they accept payments */}
      <section className={`${SECTION_PY} bg-[var(--swipe-muted)] relative overflow-hidden`} data-testid={`industry-${slug}-how-section`}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-40" style={{ backgroundImage: "radial-gradient(circle at 1.5px 1.5px, rgba(0,114,255,0.10) 1.5px, transparent 0)", backgroundSize: "22px 22px" }} />
        <div aria-hidden="true" className="absolute -top-10 right-[8%] w-[280px] h-[280px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.10)", filter: "blur(80px)" }} />
        <div className="container-swipe relative">
          <div className="grid lg:grid-cols-[1fr,1.15fr] gap-8 lg:gap-12 items-center">
            <Reveal delay={140}>
              <div className="hidden lg:block lg:order-1"><SetupIllustration /></div>
            </Reveal>
            <div className="lg:order-2">
              <Reveal><div className="overline mb-3">Typical Setup</div></Reveal>
              <Reveal delay={80}>
                <h2 className="font-display-tight text-[1.85rem] sm:text-[2.25rem] lg:text-[2.5rem] font-extrabold text-[var(--swipe-navy)] leading-[1.15]">
                  {data.howHeading}
                </h2>
              </Reveal>
              <Reveal delay={140}>
                <p className="mt-4 text-[16px] text-slate-600 leading-[1.75]">{data.howBody}</p>
              </Reveal>
            </div>
          </div>

          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {data.howPoints.map((h, i) => (
              <Reveal key={h.t} delay={80 + i * 40}>
                <div className="relative h-full bg-white border border-[var(--swipe-accent)]/20 rounded-2xl p-5 overflow-hidden transition-all duration-300 hover:border-[var(--swipe-accent)]/50 hover:shadow-[0_18px_44px_-22px_rgba(0,114,255,0.45)]">
                  <div aria-hidden="true" className="absolute -top-6 -right-6 w-16 h-16 rounded-full bg-[var(--swipe-accent)]/8" />
                  <div className="relative flex items-center gap-2 mb-2.5">
                    <span className="w-6 h-6 rounded-md bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center text-[11px] font-black">{i + 1}</span>
                    <div className="font-display text-[13px] font-black text-[var(--swipe-navy)] uppercase tracking-wide">{h.t}</div>
                  </div>
                  <p className="relative text-[14.5px] text-slate-600 leading-relaxed">{h.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION — Relevant Solutions */}
      <section className={`${SECTION_PY} bg-white relative overflow-hidden`} data-testid={`industry-${slug}-solutions-section`}>
        <div aria-hidden="true" className="absolute -top-24 -left-32 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.08)", filter: "blur(100px)" }} />
        <div aria-hidden="true" className="absolute -bottom-24 -right-16 w-[360px] h-[360px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.07)", filter: "blur(90px)" }} />
        <div className="container-swipe relative">
          <div className="grid lg:grid-cols-[1.15fr,1fr] gap-8 lg:gap-12 items-center">
            <div>
              <Reveal><div className="overline mb-3">What We Provide</div></Reveal>
              <Reveal delay={80}>
                <h2 className="font-display-tight text-[1.85rem] sm:text-[2.25rem] lg:text-[2.5rem] font-extrabold text-[var(--swipe-navy)] leading-[1.15]">
                  {data.solutionsHeading}
                </h2>
              </Reveal>
            </div>
            <Reveal variant="slide-right" delay={140}>
              <div className="hidden lg:block"><SolutionsIllustration /></div>
            </Reveal>
          </div>

          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.solutions.map((s, i) => (
              <Reveal key={s.title} delay={60 + i * 30}>
                <Link
                  to={s.to}
                  className="group relative h-full bg-white border border-[var(--swipe-accent)]/20 rounded-2xl p-5 flex flex-col overflow-hidden transition-all duration-300 ease-out hover:-translate-y-1 hover:border-[var(--swipe-accent)] hover:shadow-[0_18px_44px_-18px_rgba(0,114,255,0.55)]"
                  data-testid={`industry-${slug}-solution-${i}`}
                >
                  <div aria-hidden="true" className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-[var(--swipe-accent)]/8 transition-all duration-500 group-hover:bg-[var(--swipe-accent)]/14 group-hover:scale-110" />
                  <div aria-hidden="true" className="absolute -top-4 -right-4 w-10 h-10 rounded-lg bg-[var(--swipe-accent)]/10 rotate-12 transition-transform duration-500 group-hover:rotate-45" />
                  <div className="relative font-display text-[17px] font-black text-[var(--swipe-navy)]">{s.title}</div>
                  <p className="relative mt-2.5 text-[14.5px] text-slate-600 leading-relaxed flex-1">{s.body}</p>
                  <span className="relative mt-3 text-sm font-semibold text-[var(--swipe-navy)] inline-flex items-center gap-1.5 group-hover:text-[var(--swipe-accent)]">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION — Equipment (side illustration = industry-specific) */}
      <section className={`${SECTION_PY} bg-[var(--swipe-accent-soft)]/40 border-y border-[var(--swipe-accent)]/15 relative overflow-hidden`}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-40" style={{ backgroundImage: "radial-gradient(circle at 1.5px 1.5px, rgba(0,114,255,0.12) 1.5px, transparent 0)", backgroundSize: "26px 26px" }} />
        <div aria-hidden="true" className="absolute -top-16 -right-16 w-[360px] h-[360px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.14)", filter: "blur(100px)" }} />
        <div className="container-swipe relative">
          <div className="grid lg:grid-cols-[1fr,1fr] gap-8 lg:gap-12 items-center">
            <Reveal delay={140}>
              <div className="lg:order-1 relative rounded-2xl overflow-hidden bg-white border border-[var(--swipe-accent)]/20 shadow-[0_18px_44px_-22px_rgba(0,114,255,0.35)]">
                {Illus ? <Illus /> : null}
              </div>
            </Reveal>

            <div className="lg:order-2">
              <Reveal><div className="overline mb-3">Equipment</div></Reveal>
              <Reveal delay={80}>
                <h2 className="font-display-tight text-[1.85rem] sm:text-[2.25rem] lg:text-[2.5rem] font-extrabold text-[var(--swipe-navy)] leading-[1.15]">
                  {data.hardwareHeading}
                </h2>
              </Reveal>
              <Reveal delay={140}>
                <p className="mt-4 text-[16px] text-slate-600 leading-[1.75]">{data.hardwareBody}</p>
              </Reveal>
              <Reveal delay={200}>
                <Link to="/products" className="btn-outline-swipe mt-6 inline-flex" data-testid={`industry-${slug}-see-products`}>
                  See payment terminals <ArrowRight className="w-4 h-4" />
                </Link>
              </Reveal>
            </div>
          </div>

          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {data.hardwareItems.map((h, i) => (
              <Reveal key={h.title} delay={80 + i * 40}>
                <div className="relative h-full bg-white border border-[var(--swipe-accent)]/25 rounded-2xl p-5 overflow-hidden transition-all duration-300 hover:border-[var(--swipe-accent)] hover:shadow-[0_18px_44px_-22px_rgba(0,114,255,0.45)]">
                  <div aria-hidden="true" className="absolute -top-6 -right-6 w-16 h-16 rounded-full bg-[var(--swipe-accent)]/10" />
                  <div className="relative flex items-center gap-2 mb-2.5">
                    <span className="w-2 h-2 rounded-full bg-[var(--swipe-accent)]" />
                    <div className="font-display text-[16px] font-black text-[var(--swipe-navy)]">{h.title}</div>
                  </div>
                  <p className="relative text-[14.5px] text-slate-600 leading-relaxed">{h.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION — Why the right setup matters */}
      <section className={`${SECTION_PY} bg-white relative overflow-hidden`} data-testid={`industry-${slug}-why-section`}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-40" style={{ backgroundImage: "radial-gradient(circle at 1.5px 1.5px, rgba(0,114,255,0.08) 1.5px, transparent 0)", backgroundSize: "24px 24px" }} />
        <div aria-hidden="true" className="absolute -top-20 right-[15%] w-[300px] h-[300px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.10)", filter: "blur(90px)" }} />
        <div className="container-swipe relative">
          <div className="grid lg:grid-cols-[1.15fr,1fr] gap-8 lg:gap-12 items-center">
            <div>
              <Reveal><div className="overline mb-3">Why It Matters</div></Reveal>
              <Reveal delay={80}>
                <h2 className="font-display-tight text-[1.85rem] sm:text-[2.25rem] lg:text-[2.5rem] font-extrabold text-[var(--swipe-navy)] leading-[1.15]">
                  {data.whyHeading}
                </h2>
              </Reveal>
            </div>
            <Reveal variant="slide-right" delay={140}>
              <div className="hidden lg:block"><WhyItMattersIllustration /></div>
            </Reveal>
          </div>

          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {data.whyPoints.map((p, i) => (
              <Reveal key={i} delay={80 + i * 40}>
                <div className="relative h-full bg-white border border-[var(--swipe-accent)]/20 rounded-2xl p-5 flex gap-3 overflow-hidden transition-all duration-300 hover:border-[var(--swipe-accent)]/50 hover:shadow-[0_18px_44px_-22px_rgba(0,114,255,0.45)]">
                  <div aria-hidden="true" className="absolute -top-6 -right-6 w-16 h-16 rounded-full bg-[var(--swipe-accent)]/8" />
                  <div className="relative shrink-0 w-8 h-8 rounded-full bg-[var(--swipe-accent)] text-white flex items-center justify-center mt-0.5">
                    <Check className="w-4 h-4" strokeWidth={3} />
                  </div>
                  <p className="relative text-[14.5px] text-slate-700 leading-[1.65]">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {data.disclaimer && (
            <Reveal delay={260}>
              <div className="mt-8 p-5 lg:p-6 rounded-2xl border border-amber-200 bg-amber-50 flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" strokeWidth={2.4} />
                <p className="text-[14px] text-amber-900 leading-relaxed">{data.disclaimer}</p>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* SECTION — FAQs */}
      <section className={`${SECTION_PY} bg-[var(--swipe-muted)] relative overflow-hidden`} data-testid={`industry-${slug}-faq-section`}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-40" style={{ backgroundImage: "radial-gradient(circle at 1.5px 1.5px, rgba(0,114,255,0.10) 1.5px, transparent 0)", backgroundSize: "22px 22px" }} />
        <div aria-hidden="true" className="absolute -bottom-20 -left-16 w-[320px] h-[320px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.10)", filter: "blur(90px)" }} />
        <div className="container-swipe relative">
          <div className="grid lg:grid-cols-[1fr,1.15fr] gap-8 lg:gap-12 items-center">
            <Reveal delay={140}>
              <div className="hidden lg:block lg:order-1"><FAQIllustration /></div>
            </Reveal>
            <div className="lg:order-2">
              <Reveal><div className="overline mb-3">Common Questions</div></Reveal>
              <Reveal delay={80}>
                <h2 className="font-display-tight text-[1.85rem] sm:text-[2.25rem] lg:text-[2.5rem] font-extrabold text-[var(--swipe-navy)] leading-[1.15]">
                  Frequently asked questions
                </h2>
              </Reveal>
            </div>
          </div>

          <div className="mt-8 grid md:grid-cols-2 gap-4">
            {data.faqs.map((f, i) => (
              <Reveal key={i} delay={60 + i * 30}>
                <div className="relative h-full bg-white border border-[var(--swipe-accent)]/20 rounded-2xl p-5 overflow-hidden transition-all duration-300 hover:border-[var(--swipe-accent)]/50 hover:shadow-[0_18px_44px_-22px_rgba(0,114,255,0.45)]" data-testid={`industry-${slug}-faq-${i}`}>
                  <div aria-hidden="true" className="absolute -top-6 -right-6 w-16 h-16 rounded-full bg-[var(--swipe-accent)]/8" />
                  <div className="relative flex gap-3 items-start">
                    <div className="w-9 h-9 rounded-xl bg-[var(--swipe-accent)]/10 text-[var(--swipe-accent)] flex items-center justify-center shrink-0">
                      <HelpCircle className="w-5 h-5" strokeWidth={2.4} />
                    </div>
                    <div>
                      <div className="font-display text-[16px] font-black text-[var(--swipe-navy)] leading-snug">{f.q}</div>
                      <p className="mt-2 text-[14.5px] text-slate-600 leading-relaxed">{f.a}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION — Final CTA */}
      <section className="section bg-white" data-testid={`industry-${slug}-final-cta`}>
        <div className="container-swipe">
          <div className="rounded-3xl bg-[var(--swipe-navy)] text-white px-8 md:px-14 py-14 lg:py-16 relative overflow-hidden">
            <div aria-hidden="true" className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
            <div aria-hidden="true" className="absolute -top-20 -right-20 w-[380px] h-[380px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.20)", filter: "blur(90px)" }} />
            <div className="relative max-w-3xl">
              <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold leading-[1.12] text-white">
                Ready to build the right payment setup for your business?
              </h2>
              <p className="mt-5 text-[17px] text-white/75 leading-[1.75] max-w-2xl">
                Send us a recent processing statement and we'll walk through the setup line by line — or start with a quick call to explore the options that fit.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/get-started" className="btn-primary-swipe text-[15px] py-3.5 px-7" data-testid={`industry-${slug}-final-get-started`}>
                  Get Started <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/savings-analysis" className="btn-outline-light text-[15px] py-3.5 px-7" data-testid={`industry-${slug}-final-savings`}>
                  Request a Savings Analysis
                </Link>
                <Link to="/contact" className="btn-outline-light text-[15px] py-3.5 px-7" data-testid={`industry-${slug}-final-contact`}>
                  Contact Swipetronics
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
